<template>
  <!-- Blur lens: must be separate from the cursor visuals so backdrop-filter
       composites against the page rather than a transform ancestor -->
  <div
    class="cursor-blur-lens"
    :class="[`lens-size-${currentState}`, { 'cursor-hidden': isOverIframe || isOutsideWindow }]"
    :style="containerStyle"
  />

  <!-- Outline ring: composites above everything via mix-blend-mode: difference -->
  <div
    class="cursor-outline-ring"
    :class="[`lens-size-${currentState}`, { 'cursor-hidden': isOverIframe || isOutsideWindow }]"
    :style="containerStyle"
  />

  <!-- layer1 overlay: hoisted to fixed so mix-blend-mode composites against the page -->
  <template v-for="state in cursorStates" :key="`overlay-${state.name}`">
    <InlineSvg
      v-if="state.svg?.layer1"
      :src="state.svg.layer1"
      class="cursor-overlay-svg"
      :class="[{ active: currentState === state.name }, { 'cursor-hidden': isOverIframe || isOutsideWindow }]"
      :style="containerStyle"
    />
  </template>

  <!-- Inner container for layer2 SVGs -->
  <div class="custom-cursor-container" :class="{ 'cursor-hidden': isOverIframe || isOutsideWindow }" :style="containerStyle">
    <div class="cursor-wrapper cursor-type-blurred">
      <div
        v-for="state in cursorStates"
        :key="`state-${state.name}`"
        class="cursor-state"
        :class="[`cursor-state-${state.name}`, { active: currentState === state.name }]"
      >
        <InlineSvg v-if="state.svg?.layer2" :src="state.svg.layer2" class="cursor-svg-layer2" />
      </div>
    </div>
  </div>
</template>

<script>
export default { name: 'CursorBlurred' };
</script>

<script setup>
import { computed } from 'vue';
import InlineSvg from 'vue-inline-svg';
import { useCursorState } from './useCursorState.js';

const { currentState, containerStyle, isOverIframe, isOutsideWindow } = useCursorState();

// require() must be relative to THIS file so webpack resolves the correct static module.
function getCursorSvg(state) {
  try {
    const plain = require(`../../../../../images/cursors/BLURRED/${state}.svg`);
    return { layer1: plain, layer2: null };
  } catch (_) {}
  let layer1 = null, layer2 = null;
  try { layer1 = require(`../../../../../images/cursors/BLURRED/${state}_1.svg`); } catch (_) {}
  try { layer2 = require(`../../../../../images/cursors/BLURRED/${state}_2.svg`); } catch (_) {}
  if (layer1 || layer2) return { layer1, layer2 };
  return null;
}

const cursorStates = computed(() => [
  { name: 'default', svg: getCursorSvg('default') },
  { name: 'hover',   svg: getCursorSvg('hover') },
  { name: 'project', svg: getCursorSvg('project_hover') },
].filter(s => s.svg));
</script>

<style scoped lang="scss">
@import './cursor-shared';

// Blur lens: separate from cursor visuals so backdrop-filter composites against the page.
.cursor-blur-lens {
  @extend .cursor-overlay;
  @include cursor-lens-sizes;
  z-index: 9998;
  transform-origin: center center;
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  background: rgba(255, 255, 255, 0.10);
  background-blend-mode: exclusion;
  border-radius: 799.2px;
}

.cursor-outline-ring {
  @extend .cursor-overlay;
  @include cursor-lens-sizes;
  z-index: 10001;
  border-radius: 50%;
  mix-blend-mode: difference;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

// layer1 overlay composites against the page via mix-blend-mode: difference.
.cursor-overlay-svg {
  z-index: 10000;
  mix-blend-mode: difference;
}
</style>
